//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

/**
 * Extract loop context from URL query parameters
 * Used by embedded apps to read loop context passed from the switcher
 */
export const getLoopContextFromQueryParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const loopId = searchParams.get('loopId');
  const loopName = searchParams.get('loopName');
  const teamspaceId = searchParams.get('teamspaceId');

  return {
    teamspaceId: teamspaceId ?? null,
    loopId: loopId ?? null,
    loopName: loopName ?? null,
    isLoopContextActive: loopId !== null && loopName !== null,
  };
};

export interface UrlLoopContext {
  teamspaceId: string | null;
  loopId: string | null;
  loopName: string | null;
}

/**
 * Read the Loop/Teamspace identifiers from any URL (absolute, or relative to
 * the current origin). Single parser shared by every entry path (startup
 * seeding, `/solution/<app>` and `/embed?path=` launches) so all apps behave
 * the same. Empty or missing values come back as null; never throws.
 */
export const readContextFromUrl = (url: string): UrlLoopContext => {
  try {
    const parsed = new URL(url, window.location.origin);
    return {
      teamspaceId: parsed.searchParams.get('teamspaceId') || null,
      loopId: parsed.searchParams.get('loopId') || null,
      loopName: parsed.searchParams.get('loopName') || null,
    };
  } catch {
    return { teamspaceId: null, loopId: null, loopName: null };
  }
};

/**
 * Context for the page the app was launched with. Looks at the page's own
 * query string first (`/solution/<app>?teamspaceId=..`) and falls back to the
 * target app URL carried in the `path` parameter (`/embed?path=<app url>`).
 */
export const readLaunchContext = (): UrlLoopContext => {
  const empty = { teamspaceId: null, loopId: null, loopName: null };
  try {
    const top = readContextFromUrl(window.location.href);
    const pathParam = new URLSearchParams(window.location.search).get('path');
    let nested: UrlLoopContext = empty;
    if (pathParam) {
      try {
        nested = readContextFromUrl(decodeURIComponent(pathParam));
      } catch {
        nested = readContextFromUrl(pathParam);
      }
    }
    // Take the loop id and its name from the same source so they never mix.
    const loopSource = top.loopId ? top : nested;
    return {
      teamspaceId: top.teamspaceId ?? nested.teamspaceId,
      loopId: loopSource.loopId,
      loopName: loopSource.loopName,
    };
  } catch {
    return empty;
  }
};

/**
 * Build URL with loop context query parameters
 * Used by switcher to propagate loop context to target app
 */
export const buildUrlWithLoopContext = (
  baseUrl: string,
  teamspaceId: string,
  loopId?: string | null,
  loopName?: string | null,
): string => {
  if (!teamspaceId) return baseUrl;

  const isAbsolute = /^https?:\/\//i.test(baseUrl);
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('teamspaceId', teamspaceId);
  if (loopId && loopName) {
    url.searchParams.set('loopId', loopId);
    url.searchParams.set('loopName', loopName);
  }
  // Return a relative path when the input was relative, so callers that
  // prepend the origin themselves don't end up with a double-origin URL.
  return isAbsolute
    ? url.toString()
    : `${url.pathname}${url.search}${url.hash}`;
};
